
import React, { useMemo, useEffect, useState } from 'react';
import { Ad, VendorProfile, Neighborhood, SubscriptionPlan } from '../types';
import { supabase } from '../lib/supabase';

interface ProductModalProps {
  ad: Ad;
  allAds: Ad[];
  vendor: VendorProfile | undefined;
  neighborhood: Neighborhood | undefined;
  onClose: () => void;
  onAdClick: (ad: Ad) => void;
  onViewProfile: (vendor: VendorProfile) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({ ad, allAds, vendor, neighborhood, onClose, onAdClick, onViewProfile }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const trackView = async () => {
      if ((window as any).gtag) {
        (window as any).gtag('event', 'view_item', {
          item_id: ad.id,
          item_name: ad.title,
          neighborhood: neighborhood?.name,
          vendor_name: vendor?.name
        });
      }

      try {
        await supabase
          .from('anuncios')
          .update({ visualizacoes: (ad.views || 0) + 1 })
          .eq('id', ad.id);
      } catch (error) {
        console.error("Erro ao registrar visualização:", error);
      }
    };
    
    if (ad?.id) {
      trackView();
      setActiveImageIndex(0);
    }
  }, [ad.id]);

  const handleWhatsApp = async () => {
    if ((window as any).gtag) {
      (window as any).gtag('event', 'whatsapp_click', {
        item_id: ad.id,
        item_name: ad.title,
        vendor_name: vendor?.name,
        neighborhood: neighborhood?.name,
        source: 'product_modal'
      });
    }

    try {
      await supabase
        .from('anuncios')
        .update({ clicks_contato: (ad.clicks || 0) + 1 })
        .eq('id', ad.id);
    } catch (error) {
      console.error("Erro ao registrar clique:", error);
    }

    const text = `Olá ${vendor?.name}! Vi seu anúncio "${ad.title}" no Compre em Guarulhos e tenho interesse.`;
    window.open(`https://wa.me/${vendor?.whatsapp}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const relatedAds = useMemo(() => {
    if (!vendor) return [];
    const vendorAds = allAds.filter(a => a.vendor_id === vendor.id && a.id !== ad.id && a.is_active);
    const limit = vendor.plan === SubscriptionPlan.PRO ? 12 : 2;
    return vendorAds.slice(0, limit);
  }, [allAds, vendor, ad]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-navy/90 backdrop-blur-md" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-[2.5rem] sm:rounded-[2rem] shadow-2xl animate-in slide-in-from-bottom duration-300 no-scrollbar flex flex-col">
        
        <button onClick={onClose} className="absolute top-4 right-4 z-40 bg-navy/20 backdrop-blur-md text-white p-2 rounded-full hover:bg-navy/40 transition-all">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>

        <div className="flex-1 overflow-y-auto no-scrollbar pb-32 sm:pb-36">
          <div className="relative w-full aspect-square sm:aspect-video bg-gray-100 group">
            <img src={ad.images[activeImageIndex]} alt={ad.title} className="w-full h-full object-cover transition-all duration-700" />
            
            {/* Galeria Overlay de Miniaturas */}
            {ad.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/20 backdrop-blur-md rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                {ad.images.map((img, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-all ${activeImageIndex === idx ? 'border-brand-orange scale-110' : 'border-transparent opacity-50'}`}
                  >
                    <img src={img} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <div className="bg-brand-blue text-white px-3 py-1.5 rounded-xl text-[8px] font-black uppercase shadow-xl w-fit">📍 {neighborhood?.name}</div>
            </div>
          </div>

          <div className="p-6 sm:p-10">
            <div className="flex items-center justify-between mb-8 gap-4">
              <div className="flex items-center gap-4">
                <img src={vendor?.avatar_url} className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl border-2 border-brand-orange/40 p-0.5 object-cover" alt={vendor?.name} />
                <div>
                  <h4 className="font-title font-bold text-navy uppercase text-sm tracking-tight leading-tight line-clamp-1">{vendor?.name}</h4>
                  <button 
                    onClick={() => vendor && onViewProfile(vendor)}
                    className="text-[10px] text-brand-blue font-black uppercase tracking-widest hover:underline"
                  >
                    Ver Loja Completa →
                  </button>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="text-xl sm:text-2xl font-black text-navy block leading-none">
                  {ad.price_on_request ? 'Sob Consulta' : ad.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-4xl font-title font-black text-navy leading-none uppercase italic mb-6 tracking-tighter">{ad.title}</h2>

            <div className="bg-inputGray p-6 rounded-3xl border border-gray-100 mb-10">
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line font-medium">
                {ad.description}
              </p>
            </div>

            {/* Tags do Produto */}
            {ad.tags && ad.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-10">
                {ad.tags.map(tag => (
                  <span key={tag} className="bg-white border border-gray-100 text-[8px] font-black text-gray-400 uppercase px-3 py-1.5 rounded-full"># {tag}</span>
                ))}
              </div>
            )}

            <div className="mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-title font-black text-navy text-[10px] uppercase tracking-widest">Mais de {vendor?.name}</h3>
              </div>
              
              <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar snap-x">
                {relatedAds.length > 0 ? (
                  relatedAds.map(item => (
                    <div 
                      key={item.id} 
                      onClick={() => onAdClick(item)}
                      className="flex-shrink-0 w-36 sm:w-44 bg-inputGray rounded-2xl overflow-hidden border border-gray-100 cursor-pointer hover:border-brand-orange transition-all snap-start"
                    >
                      <img src={item.images[0]} className="w-full h-24 sm:h-28 object-cover" alt="" />
                      <div className="p-3">
                        <h4 className="text-[10px] font-bold text-navy line-clamp-1 uppercase mb-1">{item.title}</h4>
                        <p className="text-[10px] font-black text-brand-blue">
                          {item.price_on_request ? 'Consulta' : item.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 font-medium italic">Sem outros itens cadastrados.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/95 backdrop-blur-xl border-t border-gray-100 z-30 flex gap-4">
          <button onClick={onClose} className="flex-1 bg-inputGray text-gray-500 font-title font-black py-5 rounded-2xl transition-all hover:bg-gray-200 text-sm uppercase tracking-widest border border-gray-100">
            Voltar
          </button>
          <button onClick={handleWhatsApp} className="flex-[2] bg-[#25D366] text-white font-title font-black py-5 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-green-500/20 hover:scale-[1.02] active:scale-95 transition-all text-sm uppercase tracking-widest">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.632 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Chamar no Whats
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
